import { motion } from 'framer-motion';
import { CreditCard, HelpCircle, Info, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useModal } from '../../../../hooks/modalContext';
import { usePaymentOptions } from '../../../../hooks/PaymentOptionsContext';
import { ButtonSpinner } from '../../../../UI/ButtonSpinner/ButtonSpinner';
import { useState } from 'react';
import { GroupedPaymentMethod } from '../../../../utils/types';
import { usePayment } from '../../../../hooks/PaymentContext';

interface AddCardSuccessProps {}

const AddCardSuccess: React.FC<AddCardSuccessProps> = () => {
  const { loading, refreshPaymentOptions } = usePaymentOptions();
  const { groupedFee } = usePayment();
  const { setModal, modalState } = useModal();
  const navigate = useNavigate();
  const cardDetails = modalState?.newCard;
  const [isOpen, setIsOpen] = useState(false);

  return (
    <motion.div
      className='w-[554px] bg-[#FFF] rounded-[12px] flex flex-col overflow-auto relative'
      initial='hidden'
      animate='visible'
      exit='exit'
    >
      <div className='flex justify-end p-4'>
        <X
          className='cursor-pointer text-gray-500 hover:text-gray-700'
          size={24}
          onClick={() => setModal(null)}
        />
      </div>
      <div className='flex flex-col gap-4 px-10 pb-8 '>
        <div className='flex flex-col justify-center items-center gap-1'>
          {curlyCheck}
          <span className='font-bold text-[22px] text-success'>Card successfully added!</span>
        </div>
        <div className='relative w-full flex items-center ph-no-capture' data-clarity-mask='true'>
          <div className='absolute bottom-1 left-1/2 translate-x-[-50%] w-[80%] h-[60px] rounded-[20px] shadow-[0px_12px_50px_0px_rgba(33,81,236,0.80)] z-0 pointer-events-none'></div>
          <div className='relative z-5 w-full rounded-[20px] border-2 border-[#E9EDF9] bg-[linear-gradient(180deg,_#FFF_-13.57%,_#F2F4F9_113.63%)] px-[20px] py-[32px]'>
            <div className='p-2'>
              <div className='flex items-center justify-between mb-3'>
                <div className='flex items-center gap-3'>
                  <CreditCard className='w-5 h-5 text-gray-600' />

                  <span className='font-medium text-gray-900'>
                    {cardDetails?.cardBrand}{' '}
                    {cardDetails?.cardCategoryName &&
                      cardDetails.cardCategoryName.charAt(0).toUpperCase() +
                        cardDetails.cardCategoryName.slice(1).toLowerCase()}
                  </span>
                </div>
                <div className='flex items-center gap-1'>
                  <button
                    onClick={() => setIsOpen(true)}
                    className='text-blue-600 hover:text-blue-800 transition-colors'
                  >
                    <HelpCircle className='w-4 h-4' />
                  </button>
                  {isOpen && (
                    <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
                      <FeeExplanation onClose={() => setIsOpen(false)} groupedFee={groupedFee} />
                    </div>
                  )}
                </div>
              </div>
              <span className='text-xs text-gray-600'>Processing fee:</span>

              <div className='flex items-center justify-between'>
                <span className='text-2xl font-bold text-gray-900'>
                  {cardDetails?.percentageRate ? `${cardDetails.percentageRate}%` : 'N/A%'}
                </span>

                {cardDetails?.isPremiumCard && (
                  <span
                    className='text-xs px-4 py-2 rounded-full font-medium shadow-sm text-amber-900 
             bg-[linear-gradient(110deg,#fef3c7,#facc15,#fde68a,#facc15,#fef3c7)] 
             bg-[length:200%_auto] animate-[shine_3s_linear_infinite]'
                  >
                    Premium card
                  </span>
                )}
              </div>

              <p className='text-sm text-gray-600 mt-2'>
                This rate applies to your{' '}
                {cardDetails?.cardCategoryName &&
                  cardDetails.cardCategoryName.charAt(0).toUpperCase() +
                    cardDetails.cardCategoryName.slice(1).toLowerCase()}{' '}
                {cardDetails?.paymentMethod}.
              </p>
            </div>
          </div>
        </div>
        {cardDetails?.percentageRate && cardDetails?.percentageRate > '1.75' && (
          <div className='bg-amber-50 rounded-lg p-4 border border-amber-200 mt-40'>
            <div className='flex items-start gap-3'>
              <Info className='w-5 h-5 text-amber-600 mt-2' />
              <div>
                <h4 className='font-medium text-amber-900 mb-1 '>Want to pay less?</h4>
                <p className='text-sm text-amber-800 mb-2'>
                  Premium cards have higher fees. Consider using a debit card or classic credit card
                  for lower rates.
                </p>
                <button
                  className='text-sm text-amber-700 underline hover:text-amber-900 transition-colors'
                  onClick={() =>
                    setModal('add-payment-method', {
                      tenantBuildingId: undefined, // Pass tenantBuildingId if needed
                    })
                  }
                >
                  Add alternative payment method
                </button>
              </div>
            </div>
          </div>
        )}

        <button
          className='submitBtn mt-5'
          onClick={async () => {
            await refreshPaymentOptions();
            if (modalState?.payRentSetupFlow && modalState?.tenantBuildingId) {
              setModal(null);
              navigate('/pay-rent', {
                state: {
                  buildingId: modalState.tenantBuildingId,
                  initialStep: 1,
                  payPlan: modalState?.feePaymentPlan === 'direct' ? 'direct' : 'autopay',
                  syncPayFlowFromState: true,
                },
              });
              return;
            }
            setModal(null);
          }}
        >
          {loading && <ButtonSpinner />}
          Continue with this card
        </button>
      </div>
    </motion.div>
  );
};

export { AddCardSuccess };

const curlyCheck = (
  <svg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40' fill='none'>
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M16.6111 3.53119C18.4292 1.53485 21.5704 1.53485 23.3885 3.5312L25.0888 5.39825C25.5065 5.85686 26.1068 6.10551 26.7264 6.07655L29.2489 5.95866C31.9461 5.83261 34.1673 8.05376 34.0412 10.751L33.9233 13.2735C33.8944 13.8931 34.143 14.4934 34.6016 14.911L36.4687 16.6114C38.465 18.4295 38.465 21.5706 36.4687 23.3887L34.6016 25.0891C34.143 25.5067 33.8944 26.107 33.9233 26.7266L34.0412 29.2491C34.1673 31.9463 31.9461 34.1675 29.2489 34.0414L26.7264 33.9236C26.1068 33.8946 25.5065 34.1432 25.0888 34.6019L23.3885 36.4689C21.5704 38.4653 18.4292 38.4652 16.6111 36.4689L14.9108 34.6019C14.4931 34.1432 13.8929 33.8946 13.2732 33.9236L10.7507 34.0414C8.05351 34.1675 5.83236 31.9463 5.95841 29.2491L6.0763 26.7266C6.10526 26.107 5.85661 25.5067 5.398 25.0891L3.53095 23.3887C1.5346 21.5706 1.53461 18.4295 3.53095 16.6114L5.398 14.911C5.85661 14.4934 6.10526 13.8931 6.0763 13.2735L5.95841 10.751C5.83236 8.05375 8.05352 5.83261 10.7507 5.95866L13.2732 6.07655C13.8929 6.10551 14.4931 5.85686 14.9108 5.39825L16.6111 3.53119ZM21.5401 5.21451C20.7137 4.30708 19.2859 4.30708 18.4595 5.21451L16.7592 7.08156C15.8403 8.0905 14.5197 8.63753 13.1565 8.57382L10.634 8.45593C9.40801 8.39864 8.39839 9.40825 8.45569 10.6343L8.57358 13.1568C8.63729 14.5199 8.09026 15.8406 7.08132 16.7594L5.21426 18.4597C4.30683 19.2861 4.30683 20.714 5.21426 21.5404L7.08132 23.2407C8.09026 24.1595 8.63729 25.4802 8.57358 26.8433L8.45569 29.3658C8.39839 30.5919 9.40801 31.6015 10.634 31.5442L13.1565 31.4263C14.5197 31.3626 15.8403 31.9096 16.7592 32.9185L18.4595 34.7856C19.2859 35.693 20.7137 35.693 21.5401 34.7856L23.2404 32.9185C24.1593 31.9096 25.4799 31.3626 26.8431 31.4263L29.3656 31.5442C30.5916 31.6015 31.6012 30.5919 31.5439 29.3658L31.426 26.8433C31.3623 25.4802 31.9094 24.1595 32.9183 23.2407L34.7853 21.5404C35.6928 20.714 35.6928 19.2861 34.7853 18.4597L32.9183 16.7594C31.9094 15.8406 31.3623 14.5199 31.426 13.1568L31.5439 10.6343C31.6012 9.40825 30.5916 8.39864 29.3656 8.45593L26.8431 8.57382C25.4799 8.63753 24.1593 8.0905 23.2404 7.08156L21.5401 5.21451Z'
      fill='#089B19'
    />
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M14.1159 19.1162C14.6041 18.628 15.3955 18.628 15.8837 19.1162L18.3331 21.5656L24.1159 15.7828C24.6041 15.2947 25.3955 15.2947 25.8837 15.7828C26.3718 16.271 26.3718 17.0624 25.8837 17.5506L19.217 24.2173C18.7289 24.7054 17.9374 24.7054 17.4493 24.2173L14.1159 20.8839C13.6278 20.3958 13.6278 19.6043 14.1159 19.1162Z'
      fill='#089B19'
    />
  </svg>
);
interface FeeExplanationProps {
  groupedFee: GroupedPaymentMethod[];
  onClose: () => void;
}
const FeeExplanation: React.FC<FeeExplanationProps> = ({ groupedFee, onClose }) => (
  <div className='mt-4 px-6 py-4 bg-blue-50 rounded-lg border border-blue-200 max-w-[500px]'>
    <div className='flex justify-end '>
      <X className='cursor-pointer text-gray-500 hover:text-gray-700' size={14} onClick={onClose} />
    </div>
    <div className='flex items-start gap-3 p-6'>
      <div className='flex-1'>
        <p className='text-sm text-blue-800 mb-3'>
          Processing fees depend on your card type and issuer. Premium cards typically have higher
          fees due to rewards and benefits programs.
        </p>
        <div className='space-y-2 '>
          {groupedFee.map((method) =>
            method.companies.map((company) => (
              <div key={company.name}>
                <div className='text-black-700 font-medium mb-1'>{company.name}</div>
                {company.categories.map((cat) => (
                  <div
                    key={cat.id}
                    className='flex justify-between items-center py-1 border-b border-blue-200'
                  >
                    <span className='text-blue-700'>
                      {cat.name.charAt(0).toUpperCase() + cat.name.slice(1)}
                    </span>
                    <span className='font-medium text-blue-900'>
                      {cat.directRate &&
                      cat.subscriptionRate &&
                      cat.directRate !== cat.subscriptionRate
                        ? `${cat.directRate} / ${cat.subscriptionRate}`
                        : cat.directRate || cat.subscriptionRate || '-'}
                    </span>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  </div>
);
